import { superstructResolver } from '@hookform/resolvers/superstruct';
import { PrimaryButton } from '@rexlabs/button';
import { TextInput } from '@rexlabs/text-input';
import React, { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MegaModalContent } from 'src/components/elements/modal/MegaModalContent';
import { FormControl, FormInput, FormLabel } from 'src/components/form';
import { useModalState } from 'src/hooks';
import { EmailRequest } from '../types';
import { leadCaptureEmailSchema } from '../utils/schemas';

const errorStyles: React.CSSProperties = {
  fontSize: '12px',
  color: '#d32f2f',
  marginTop: '4px',
  display: 'block'
};

const helperTextStyles: React.CSSProperties = {
  fontSize: '12px',
  color: '#666',
  marginTop: '4px',
  display: 'block'
};

export type AdditionalLeadEmailModalProps = {
  onSubmit: (values: EmailRequest) => Promise<void> | void;
  hasMultipleEmailsFeature?: boolean;
  currentEmailCount?: number;
};

export const AdditionalLeadEmailModal = ({
  onSubmit,
  hasMultipleEmailsFeature = false,
  currentEmailCount = 0
}: AdditionalLeadEmailModalProps): JSX.Element => {
  const [, setModalOpen] = useModalState();
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
    watch,
    setError,
    clearErrors
  } = useForm<EmailRequest>({
    mode: 'onChange',
    resolver: superstructResolver(leadCaptureEmailSchema),
    shouldUnregister: true
  });

  // Watch the email field for changes to validate against feature flag
  const emailValue = watch('email');
  
  // Check if the form should be disabled due to email limit
  const emailCount = emailValue ? emailValue.split(',').map(e => e.trim()).filter(e => e.length > 0).length : 0;
  const totalEmailsAfterAdd = currentEmailCount + emailCount;
  const exceedsLimit = !hasMultipleEmailsFeature && totalEmailsAfterAdd > 2;
  
  // Set custom error if exceeds limit
  useEffect(() => {
    if (exceedsLimit && emailValue) {
      setError('email', {
        type: 'manual',
        message: `You can only have up to 2 lead capture email addresses per campaign. You currently have ${currentEmailCount} and are trying to add ${emailCount} more.`
      });
    } else if (!exceedsLimit) {
      clearErrors('email');
    }
  }, [exceedsLimit, setError, clearErrors, currentEmailCount, emailCount, emailValue]);
  const form = useRef<HTMLFormElement>(null);

  const handleFormSubmit = async (value: EmailRequest): Promise<void> => {
    // Prevent submission if exceeds limit
    if (exceedsLimit) {
      return;
    }
    
    try {
      await onSubmit(value);
      setModalOpen(false); // Close modal on successful submission
    } catch (error) {
      // Don't close modal if submission fails
      console.error('Failed to submit email:', error);
    }
  };

  return (
    <MegaModalContent
      title='Additional email addresses'
      actions={[
        {
          label: 'Save',
          Component: <PrimaryButton type='submit' isDisabled={!isValid || isSubmitting || exceedsLimit} />,
          onClick: () => form.current?.requestSubmit(),
          closes: false
        },
        {
          label: 'Cancel',
          closes: true
        }
      ]}
    >
      <form ref={form} onSubmit={handleSubmit(handleFormSubmit)}>
        <Controller
          control={control}
          name='email'
          render={({ field, fieldState: { error } }) => (
            <FormControl isInvalid={error !== undefined}>
              <FormLabel>Email addresses</FormLabel>
              <FormInput
                Input={TextInput}
                {...field}
                // @ts-expect-error FormInput doesn't have good generics
                placeholder='email1@example.com, email2@example.com'
              />
              {(error || exceedsLimit) && (
                <span style={errorStyles}>
                  {exceedsLimit
                    ? `You can only have up to 2 lead capture email addresses per campaign. You currently have ${currentEmailCount} and are trying to add ${emailCount} more.`
                    : error?.message}
                </span>
              )}
              {!error && !exceedsLimit && (
                <span style={helperTextStyles}>
                  You can add multiple email addresses separated by commas
                  {!hasMultipleEmailsFeature && currentEmailCount === 1 &&
                    ' (maximum of 2 total emails per campaign)'}
                </span>
              )}
            </FormControl>
          )}
        />
      </form>
    </MegaModalContent>
  );
};
